import { useAtomValue } from 'jotai';
import { useCallback, useMemo } from 'react';
import { IConfig } from '../../config';
import { Account, inventoryDetailsAtom } from '../../state';
import { InstrumentCategoryEnum, Inventory, InventoryWithListedInstrument } from '../../types';
import { PortfolioCompositionGridItem } from '../../types/portfolio/portfolioCompositionGridItem';
import {
  BaseCurrency,
  groupBy,
  groupByAndMergeNumericValues,
  isDefined,
  mergeNumericValues,
  MergeNumericValuesSettings,
} from '../../utils';
import { useCurrencyExchangeRateRT } from '../core/useCurrencyExchangeRateRT';
import { getInitialPNl, getInventoryInitPriceValue, getInventoryListByAccountList, htmlNanoId } from './inventoryUtils';
import { PortfolioUtils } from './portfolioUtils';

const subElementGroupByAdditionalProps: MergeNumericValuesSettings<Inventory> = {
  onlyFirstValueFields: ['exchangeRate', 'decimalPlace', 'marketPriceSingle'],
  avgFields: ['initValue'],
  weightedAvgField: 'position',
};

export const usePortfolioCompositionGridCommon = (config: IConfig, selectedAccountList: Account[]) => {
  const inventoryDetails = useAtomValue(inventoryDetailsAtom);
  const { getCurrencyExchangeRateRealTime } = useCurrencyExchangeRateRT();

  const inventories = useMemo(
    () => (inventoryDetails.isLoaded ? inventoryDetails.inventories : []),
    [inventoryDetails],
  );

  /** @private */
  const createGridItem = (
    inventory: Inventory | InventoryWithListedInstrument,
    accumulatedValues?: {
      position: number;
      lockedQuantity?: number;
      initPriceValue: number;
      grossProfitLossHuf: number;
      grossProfitLoss: number;
      value?: number;
      valueInHuf?: number;
      marketPriceSingle?: number;
    },
  ): PortfolioCompositionGridItem => {
    const rateForValueInHuf = getCurrencyExchangeRateRealTime(inventory.marketPriceCcy, BaseCurrency);
    const value = accumulatedValues?.value || inventory.position * inventory.marketPriceSingle;
    const valueInHuf =
      accumulatedValues?.valueInHuf || inventory.position * inventory.marketPriceSingle * rateForValueInHuf;
    const position = accumulatedValues?.position || inventory.position;
    const initPriceValue = accumulatedValues?.initPriceValue || inventory.initPriceValue;
    const grossProfitLossHuf = accumulatedValues?.grossProfitLossHuf || inventory.grossProfitLossHuf;
    const grossProfitLoss = accumulatedValues?.grossProfitLoss || inventory.grossProfitLoss;
    const marketPriceSingle = accumulatedValues?.marketPriceSingle || inventory.marketPriceSingle;

    const rateForPnl = getCurrencyExchangeRateRealTime(inventory.marketPriceCcy, inventory.initCcy);
    const { pNL, pNLPercentage } = getInitialPNl({
      instrumentCategory: inventory.instrumentCategory as InstrumentCategoryEnum, // no need to depend on accumulatedValues, because this field is not accumulated
      position,
      marketPriceSingle,
      initPriceValue,
      rate: rateForPnl,
      grossProfitLoss: inventory.initCcy === config.NetBroker.BaseCurrency ? grossProfitLossHuf : grossProfitLoss,
      ric: inventory.ric,
    });

    return {
      id: accumulatedValues ? inventory.id : htmlNanoId(),
      instrumentName: inventory.ticker,
      instrument: (inventory as InventoryWithListedInstrument).instrument,
      instrumentCategory: inventory.instrumentCategory,
      accountNumber: inventory.accountNumber,
      name: inventory.instrumentName + (inventory.namePostfix ?? ''),
      quantity: accumulatedValues?.position || inventory.position,
      lockedQuantity: accumulatedValues?.lockedQuantity ?? inventory.lockedQuantity ?? 0,
      // if the market price currency is the same as the base currency, we don't have to show the value
      value: inventory.initCcy === config.NetBroker.BaseCurrency ? undefined : value,
      initValue: inventory.initValue,
      valueInHuf: valueInHuf,
      investmentAmount: accumulatedValues?.initPriceValue || inventory.initPriceValue,
      exchangeRate: inventory.exchangeRate,
      pNL,
      pNLPercentage,
      weight: 0,
      currency: inventory.initCcy,
      $subElements: undefined,
      marketPriceCcy: inventory.marketPriceCcy,
      initCcy: inventory.initCcy,
      ric: inventory.ric,
    };
  };

  /**
   * Returns portfolio items filtered by account number
   */
  const getInstrumentsListByAccountForGrid = useCallback(
    (accountNumberFilter: string | null, instrumentCategory?: string): Array<PortfolioCompositionGridItem> => {
      const inventoryListByAccountList = getInventoryListByAccountList(
        inventories,
        accountNumberFilter || selectedAccountList,
      );
      const filteredInventories = isDefined(instrumentCategory)
        ? inventoryListByAccountList.filter(_ => _.instrumentCategory === instrumentCategory)
        : inventoryListByAccountList;

      // we will create rows for the grid based on this group, because every row will represent an inventory item
      // which can be opened to see the sub elements
      const inventoryItemsGroupedByInstrument: Record<string, Array<Inventory>> = groupBy(filteredInventories, 'id');

      const gridItems = Object.keys(inventoryItemsGroupedByInstrument).reduce((res, inventoryRowId) => {
        // this is a list of inventories of the same element that needed to be grouped,
        // but if an account is selected this list is grouped and merged by account number
        const inventoriesOfInstrument = isDefined(accountNumberFilter)
          ? inventoryItemsGroupedByInstrument[inventoryRowId]
          : groupByAndMergeNumericValues<Inventory, Inventory>(
              inventoryItemsGroupedByInstrument[inventoryRowId],
              'accountNumber',
              subElementGroupByAdditionalProps,
            );

        if (inventoriesOfInstrument.length === 1) {
          res.push(
            createGridItem(inventoriesOfInstrument[0], {
              position: inventoriesOfInstrument[0].position,
              lockedQuantity: inventoriesOfInstrument[0].lockedQuantity ?? 0,
              initPriceValue: getInventoryInitPriceValue(inventoriesOfInstrument[0]),
              grossProfitLossHuf: inventoriesOfInstrument[0].grossProfitLossHuf,
              grossProfitLoss: inventoriesOfInstrument[0].grossProfitLoss,
            }),
          );
        } else if (inventoriesOfInstrument.length > 1) {
          const accumulatedValues = mergeNumericValues<
            Inventory,
            Inventory & {
              lockedQuantity?: number;
              value: number;
              valueInHuf: number;
            }
          >(inventoriesOfInstrument, {
            ...subElementGroupByAdditionalProps,
            reducer: (acc, next) => {
              const rate = getCurrencyExchangeRateRealTime(next.marketPriceCcy, BaseCurrency);
              acc.value = (acc.value || 0) + next.position * next.marketPriceSingle;
              acc.valueInHuf = (acc.valueInHuf || 0) + next.position * next.marketPriceSingle * rate;
              return acc;
            },
          });

          const createdItem = createGridItem(inventoriesOfInstrument[0], accumulatedValues);
          createdItem.$subElements = accountNumberFilter
            ? undefined
            : inventoriesOfInstrument.map(_ => createGridItem(_));
          res.push(createdItem);
        }
        return res;
      }, [] as Array<PortfolioCompositionGridItem>);

      PortfolioUtils.calculatePortfolioWeight(gridItems);
      return gridItems;
    },
    [inventories, selectedAccountList, getCurrencyExchangeRateRealTime],
  );

  /**
   * Returns portfolio items filtered by account number and instrument category
   */
  const getInstrumentListForGrid = useCallback(
    (accountNumberFilter: string | null, instrumentCategory: string): Array<PortfolioCompositionGridItem> => {
      if (isDefined(accountNumberFilter)) {
        const filteredInventories = getInventoryListByAccountList(
          inventories,
          accountNumberFilter || selectedAccountList,
        ).filter(_ => _.instrumentCategory === instrumentCategory);

        const uniqueInstruments = groupByAndMergeNumericValues(
          filteredInventories,
          'id',
          subElementGroupByAdditionalProps,
        );

        const gridItems = uniqueInstruments.map(_ => createGridItem(_));

        PortfolioUtils.calculatePortfolioWeight(gridItems);
        return gridItems;
      }

      // if no account number is selected, we have to group by account number
      return getInstrumentsListByAccountForGrid(accountNumberFilter, instrumentCategory);
    },
    [inventories, selectedAccountList, getInstrumentsListByAccountForGrid],
  );

  return {
    getInstrumentListForGrid,
    getInstrumentsListByAccountForGrid,
  };
};
